// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div>

    <!-- ── 1. Greeting ───────────────────────────────────────────────────── -->
    <div class="greeting-row">
      <div>
        <h1 class="greeting-title">{{ greeting }}, {{ displayName }}</h1>
        <p class="greeting-sub">{{ greetingSub }}</p>
      </div>
    </div>

    <!-- ── 1. Zone selector + refresh ────────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;">
      <a-row justify="space-between" align="middle">
        <a-col :xs="24" :sm="16" :md="12" :lg="8">
          <a-select
            v-model:value="zoneSelectedKey"
            :placeholder="$t('label.select.a.zone')"
            style="width: 100%;"
            show-search
            option-filter-prop="label"
            :filter-option="filterZone"
            @change="changeZone"
          >
            <a-select-option
              v-for="(zone, index) in zones"
              :key="index"
              :label="zone.name"
            >
              <resource-icon
                v-if="zone.icon && zone.icon.base64image"
                :image="zone.icon.base64image"
                size="2x"
                style="margin-right: 5px;"
              />
              <GlobalOutlined v-else style="margin-right: 5px;" />
              {{ zone.name }}
            </a-select-option>
          </a-select>
        </a-col>
        <a-col>
          <a-button
            :loading="loading || capacityLoading"
            @click="() => { updateData(zoneSelected); listAlerts(); listEvents(); }"
          >
            <template #icon><ReloadOutlined /></template>
            {{ $t('label.fetch.latest') }}
          </a-button>
        </a-col>
      </a-row>
    </a-card>

    <!-- ── 2. Capacity tabs ──────────────────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;" :loading="capacityLoading" :body-style="{ padding: 0 }">
      <a-tabs v-model:activeKey="capacityTab" size="small" style="padding: 0 12px;">

        <!-- Compute tab -->
        <a-tab-pane key="compute">
          <template #tab><HddOutlined /> {{ $t('label.compute') || 'Compute' }}</template>
          <div class="capacity-row" style="padding: 12px;">
            <template v-for="ctype in computeItems" :key="ctype">
              <div class="capacity-item capacity-item--large">
                <div class="capacity-header">
                  <span class="capacity-label">{{ $t(ts[ctype]) }}</span>
                  <span class="capacity-used" :class="getCapacityClass(capacityPct(ctype))">
                    {{ displayData(ctype, statsMap[ctype].capacityused) }}
                    <span class="capacity-sep">/</span>
                    {{ displayData(ctype, statsMap[ctype].capacitytotal) }}
                  </span>
                </div>
                <a-progress :percent="capacityPct(ctype)" :show-info="false" size="small"
                  :stroke-color="getCapacityColor(capacityPct(ctype))" trail-color="#f0f0f0" />
                <div class="capacity-footer">
                  <span>{{ capacityPct(ctype).toFixed(1) }}% allocated</span>
                  <span class="capacity-free">{{ displayData(ctype, statsMap[ctype].capacitytotal - statsMap[ctype].capacityused) }} free</span>
                </div>
              </div>
            </template>
            <a-empty v-if="computeItems.length === 0" :description="$t('label.no.data') || 'No data'" style="padding: 16px 0; flex: 1;" />
          </div>
        </a-tab-pane>

        <!-- Storage tab -->
        <a-tab-pane key="storage">
          <template #tab><HddOutlined /> {{ $t('label.storage') || 'Storage' }}</template>
          <div class="capacity-row" style="padding: 12px;">
            <template v-for="ctype in storageItems" :key="ctype">
              <div class="capacity-item">
                <div class="capacity-header">
                  <span class="capacity-label" :title="$t(ts[ctype])">{{ $t(ts[ctype]) }}</span>
                  <span class="capacity-used" :class="getCapacityClass(capacityPct(ctype))">
                    {{ displayData(ctype, statsMap[ctype].capacityused) }}
                  </span>
                </div>
                <a-progress :percent="capacityPct(ctype)" :show-info="false" size="small"
                  :stroke-color="getCapacityColor(capacityPct(ctype))" trail-color="#f0f0f0" />
                <div class="capacity-footer">
                  <span>{{ capacityPct(ctype).toFixed(1) }}%</span>
                  <span class="capacity-free">/ {{ displayData(ctype, statsMap[ctype].capacitytotal) }}</span>
                </div>
              </div>
            </template>
            <a-empty v-if="storageItems.length === 0" :description="$t('label.no.data') || 'No data'" style="padding: 16px 0; flex: 1;" />
          </div>
        </a-tab-pane>

        <!-- Network tab -->
        <a-tab-pane key="network">
          <template #tab><ApartmentOutlined /> {{ $t('label.network') || 'Network' }}</template>
          <div class="capacity-row" style="padding: 12px;">
            <template v-for="ctype in networkItems" :key="ctype">
              <div class="capacity-item">
                <div class="capacity-header">
                  <span class="capacity-label" :title="$t(ts[ctype])">{{ $t(ts[ctype]) }}</span>
                  <span class="capacity-used" :class="getCapacityClass(capacityPct(ctype))">
                    {{ statsMap[ctype].capacityused }}
                  </span>
                </div>
                <a-progress :percent="capacityPct(ctype)" :show-info="false" size="small"
                  :stroke-color="getCapacityColor(capacityPct(ctype))" trail-color="#f0f0f0" />
                <div class="capacity-footer">
                  <span>{{ capacityPct(ctype).toFixed(1) }}%</span>
                  <span class="capacity-free">/ {{ statsMap[ctype].capacitytotal }}</span>
                </div>
              </div>
            </template>
            <a-empty v-if="networkItems.length === 0" :description="$t('label.no.data') || 'No data'" style="padding: 16px 0; flex: 1;" />
          </div>
        </a-tab-pane>

      </a-tabs>
    </a-card>

    <!-- ── 5. Infrastructure count pills ──────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;" :loading="loading">
      <div class="counts-row">

        <router-link :to="{ path: '/host', query: { zoneid: zoneSelected.id } }" class="count-pill" :class="data.alertHosts > 0 ? 'count-pill--alert' : ''">
          <DatabaseOutlined class="count-icon" />
          <span class="count-value">{{ data.totalHosts }}</span>
          <span class="count-label">{{ $t('label.hosts') }}</span>
          <a-badge v-if="data.alertHosts > 0" :count="data.alertHosts" style="margin-left: 2px;" />
          <CheckCircleOutlined v-else class="count-ok" />
        </router-link>

        <router-link :to="{ path: '/pod', query: { zoneid: zoneSelected.id } }" class="count-pill">
          <AppstoreOutlined class="count-icon" />
          <span class="count-value">{{ data.pods }}</span>
          <span class="count-label">{{ $t('label.pods') }}</span>
        </router-link>

        <router-link :to="{ path: '/cluster', query: { zoneid: zoneSelected.id } }" class="count-pill">
          <ClusterOutlined class="count-icon" />
          <span class="count-value">{{ data.clusters }}</span>
          <span class="count-label">{{ $t('label.clusters') }}</span>
        </router-link>

        <router-link :to="{ path: '/storagepool', query: { zoneid: zoneSelected.id } }" class="count-pill">
          <HddOutlined class="count-icon" />
          <span class="count-value">{{ data.pools }}</span>
          <span class="count-label">{{ $t('label.primary.storage') }}</span>
        </router-link>

        <router-link :to="{ path: '/vm', query: { zoneid: zoneSelected.id, projectid: '-1' } }" class="count-pill">
          <CloudServerOutlined class="count-icon" />
          <span class="count-value">{{ data.instances }}</span>
          <span class="count-label">{{ $t('label.instances') }}</span>
        </router-link>

        <router-link :to="{ path: '/vm', query: { state: 'stopped', filter: 'stopped' } }" class="count-pill count-pill--muted">
          <CloudServerOutlined class="count-icon" />
          <span class="count-value">{{ data.stopped }}</span>
          <span class="count-label">{{ $t('label.stopped') }}</span>
        </router-link>

        <router-link :to="{ path: '/systemvm', query: { zoneid: zoneSelected.id } }" class="count-pill">
          <ThunderboltOutlined class="count-icon" />
          <span class="count-value">{{ data.systemvms }}</span>
          <span class="count-label">{{ $t('label.system.vms') }}</span>
        </router-link>

        <router-link :to="{ path: '/router', query: { zoneid: zoneSelected.id } }" class="count-pill">
          <ForkOutlined class="count-icon" />
          <span class="count-value">{{ data.routers }}</span>
          <span class="count-label">{{ $t('label.virtual.routers') }}</span>
        </router-link>

      </div>
    </a-card>

    <!-- Tabbed resource table: full width -->
    <a-card size="small" :body-style="{ padding: 0 }" style="margin-bottom: 12px;">
          <a-tabs v-model:activeKey="activeTab" size="small" style="padding: 0 12px;">

            <!-- Instances tab -->
            <a-tab-pane
              key="vms"
              v-if="'listVirtualMachines' in $store.getters.apis"
            >
              <template #tab>
                <CloudServerOutlined /> {{ $t('label.instances') }}
                <a-badge :count="vmTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="vms"
                :columns="vmColumns"
                :loading="loadingVms"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/vm/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'resources'">
                    <a-tag>{{ record.cpunumber }} vCPU</a-tag>
                    <a-tag>{{ formatMemory(record.memory) }}</a-tag>
                  </template>
                  <template v-else-if="column.key === 'ipaddress'">
                    <span v-for="nic in (record.nic || [])" :key="nic.id">
                      <span v-if="nic.ipaddress">{{ nic.ipaddress }}<br/></span>
                    </span>
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/vm' }">{{ $t('label.view.all') || 'View all' }} {{ vmTotal }} {{ $t('label.instances') }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Kubernetes tab -->
            <a-tab-pane
              key="k8s"
              v-if="'listKubernetesClusters' in $store.getters.apis"
            >
              <template #tab>
                <ClusterOutlined /> {{ $t('label.kubernetes') || 'Kubernetes' }}
                <a-badge :count="k8sTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="k8sClusters"
                :columns="k8sColumns"
                :loading="loadingK8s"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/kubernetes/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'size'">
                    {{ record.size }} node{{ record.size !== 1 ? 's' : '' }}
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/kubernetes' }">{{ $t('label.view.all') || 'View all' }} {{ k8sTotal }} {{ $t('label.kubernetes.cluster') }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Autoscale groups tab -->
            <a-tab-pane
              key="autoscale"
              v-if="'listAutoScaleVmGroups' in $store.getters.apis"
            >
              <template #tab>
                <SyncOutlined /> {{ $t('label.autoscale.vmgroup') || 'Autoscale' }}
                <a-badge :count="autoscaleTotal" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="autoscaleGroups"
                :columns="autoscaleColumns"
                :loading="loadingAutoscale"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/autoscalevmgroup/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                  <template v-else-if="column.key === 'size'">
                    {{ record.vmcount || 0 }} / {{ record.maxmembers }}
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/autoscalevmgroup' }">{{ $t('label.view.all') || 'View all' }}</router-link>
              </div>
            </a-tab-pane>

            <!-- Networks tab -->
            <a-tab-pane
              key="networks"
              v-if="'listNetworks' in $store.getters.apis"
            >
              <template #tab>
                <ApartmentOutlined /> {{ $t('label.networks') || 'Networks' }}
                <a-badge :count="counts.networks" :overflow-count="999" style="margin-left: 4px;" />
              </template>
              <a-table
                :dataSource="networks"
                :columns="networkColumns"
                :loading="loadingNetworks"
                :pagination="false"
                size="small"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <router-link :to="{ path: '/guestnetwork/' + record.id }">{{ record.name }}</router-link>
                  </template>
                  <template v-else-if="column.key === 'state'">
                    <status :text="record.state" />
                  </template>
                </template>
              </a-table>
              <div class="tab-footer">
                <router-link :to="{ path: '/guestnetwork' }">{{ $t('label.view.all') || 'View all' }}</router-link>
              </div>
            </a-tab-pane>

          </a-tabs>
    </a-card>

    <!-- ── 6. Alerts + Events ─────────────────────────────────────────────── -->
    <a-row :gutter="[12, 12]">

      <!-- Alerts -->
      <a-col :xs="24" :xl="12">
        <a-card size="small" :loading="loading">
          <template #title><FlagOutlined /> {{ $t('label.alerts') }}</template>
          <template #extra>
            <router-link :to="{ path: '/alert' }">{{ $t('label.view.all') || 'View all' }}</router-link>
          </template>
          <div class="event-list">
            <div
              v-for="alert in alerts"
              :key="alert.id"
              class="event-compact-row"
            >
              <span class="event-dot dot-red"></span>
              <div class="event-compact-body">
                <div class="event-compact-top">
                  <router-link :to="{ path: '/alert/' + alert.id }" class="event-type">{{ alert.name }}</router-link>
                  <span class="event-compact-time">{{ $toLocaleDate(alert.sent) }}</span>
                </div>
                <div class="event-compact-desc">{{ alert.description }}</div>
              </div>
            </div>
            <a-empty
              v-if="alerts.length === 0"
              :description="$t('label.no.alerts') || 'No alerts'"
              style="padding: 8px 0;"
            >
              <template #image>
                <CheckCircleOutlined style="font-size: 32px; color: #52c41a;" />
              </template>
            </a-empty>
          </div>
        </a-card>
      </a-col>

      <!-- Events -->
      <a-col :xs="24" :xl="12">
        <a-card size="small" :loading="loading">
          <template #title><ScheduleOutlined /> {{ $t('label.events') }}</template>
          <template #extra>
            <router-link :to="{ path: '/event' }">{{ $t('label.view.all') || 'View all' }}</router-link>
          </template>
          <div class="event-list">
            <div
              v-for="event in events"
              :key="event.id"
              class="event-compact-row"
            >
              <span class="event-dot" :class="'dot-' + getEventColour(event)"></span>
              <div class="event-compact-body">
                <div class="event-compact-top">
                  <router-link :to="{ path: '/event/' + event.id }" class="event-type">{{ event.type }}</router-link>
                  <span class="event-compact-time">{{ $toLocaleDate(event.created) }}</span>
                </div>
                <div class="event-compact-desc">
                  <resource-label
                    :resourceType="event.resourcetype"
                    :resourceId="event.resourceid"
                    :resourceName="event.resourcename"
                  />
                </div>
              </div>
            </div>
          </div>
        </a-card>
      </a-col>

    </a-row>

  </div>
</template>

<script>
import { getAPI } from '@/api'
import {
  ReloadOutlined, GlobalOutlined, DatabaseOutlined, AppstoreOutlined, DesktopOutlined,
  ClusterOutlined, HddOutlined, CloudServerOutlined, ThunderboltOutlined,
  ForkOutlined, FlagOutlined, ScheduleOutlined, ApartmentOutlined,
  CheckCircleOutlined
} from '@ant-design/icons-vue'
import ChartCard from '@/components/widgets/ChartCard'
import ResourceIcon from '@/components/view/ResourceIcon'
import ResourceLabel from '@/components/widgets/ResourceLabel'
import Status from '@/components/widgets/Status'
import dayjs from 'dayjs'
import store from '@/store'


// ── Multilingual greetings ────────────────────────────────────────────────
const GREETINGS = [
  'Hello', 'Hola', 'Bonjour', 'Ciao', 'Hallo', 'Hej'
]

export default {
  name: 'CapacityDashboard',

  components: {
    ChartCard, ResourceIcon, ResourceLabel, Status,
    ReloadOutlined, GlobalOutlined, DatabaseOutlined, AppstoreOutlined, DesktopOutlined, DesktopOutlined,
    ClusterOutlined, HddOutlined, CloudServerOutlined, ThunderboltOutlined,
    ForkOutlined, FlagOutlined, ScheduleOutlined, ApartmentOutlined,
    CheckCircleOutlined
  },

  data () {
    // Pick a greeting randomly once per mount
    const greetingWord = GREETINGS[Math.floor(Math.random() * GREETINGS.length)]
    const now          = dayjs()
    const hour         = now.hour()
    let sub = 'Good evening.'
    if (hour < 12) sub = 'Good morning.'
    else if (hour < 18) sub = 'Good afternoon.'

    return {
      greeting:    greetingWord,
      greetingSub: sub,
      loading:         false,
      capacityLoading: false,
      capacityTab:     'compute',
      alerts:  [],
      events:  [],
      zones:   [],
      zoneSelected: {},
      statsMap: {},

      vms:      [],
      vmTotal:  0,

      k8sClusters: [],
      k8sTotal:    0,

      autoscaleGroups: [],
      autoscaleTotal:  0,

      networks:      [],

      events:  [],
      backups: [],

      usageRecords:  [],
      hasUsageData:  false,
      currentMonthLabel: dayjs().format('MMMM YYYY'),
      counts: {
        running: 0, stopped: 0, kubernetes: 0,
        volumes: 0, snapshots: 0, networks: 0,
        vpcs: 0, ips: 0, templates: 0
      },
      data: {
        pods: 0, clusters: 0, totalHosts: 0, alertHosts: 0,
        pools: 0, instances: 0, stopped: 0, systemvms: 0, routers: 0
      },
      ts: {
        CPU:                          'label.cpu',
        CPU_CORE:                     'label.cpunumber',
        DIRECT_ATTACHED_PUBLIC_IP:    'label.direct.ips',
        GPU:                          'label.gpu',
        LOCAL_STORAGE:                'label.local.storage',
        MEMORY:                       'label.memory',
        PRIVATE_IP:                   'label.management.ips',
        SECONDARY_STORAGE:            'label.secondary.storage',
        BACKUP_STORAGE:               'label.backup.storage',
        OBJECT_STORAGE:               'label.object.storage',
        STORAGE:                      'label.primary.storage.used',
        STORAGE_ALLOCATED:            'label.primary.storage.allocated',
        VIRTUAL_NETWORK_PUBLIC_IP:    'label.public.ips',
        VLAN:                         'label.vlan',
        VIRTUAL_NETWORK_IPV6_SUBNET:  'label.ipv6.subnets'
      }
    }
  },

  computed: {
    displayName () {
      return this.$store.getters.userInfo?.firstname ||
             this.$store.getters.userInfo?.username  ||
             this.$store.getters.userInfo?.account   || 'yipie'
    },
    zoneSelectedKey () {
      if (this.zones.length === 0) return this.zoneSelected.name
      return this.zones.findIndex(z => z.id === this.zoneSelected.id)
    },

    // Filter to only types present in statsMap — avoids v-if+v-for on same element
    computeItems () {
      return ['CPU_CORE', 'MEMORY'].filter(t => this.statsMap[t])
    },
    storageItems () {
      return ['STORAGE', 'STORAGE_ALLOCATED', 'SECONDARY_STORAGE', 'BACKUP_STORAGE', 'LOCAL_STORAGE'].filter(t => this.statsMap[t])
    },
    networkItems () {
      return ['VIRTUAL_NETWORK_PUBLIC_IP', 'DIRECT_ATTACHED_PUBLIC_IP', 'VLAN', 'VIRTUAL_NETWORK_IPV6_SUBNET', 'PRIVATE_IP'].filter(t => this.statsMap[t])
    },

    resourceCounts () {
      const apis = this.$store.getters.apis
      return [
        { key: 'running',    apiKey: 'listVirtualMachines',    labelKey: 'label.running.vms',  icon: 'CloudServerOutlined',    count: this.counts.running,    path: '/vm'              },
        { key: 'stopped',    apiKey: 'listVirtualMachines',    labelKey: 'label.stopped.vms',  icon: 'CloudServerOutlined',    count: this.counts.stopped,    path: '/vm'              },
        { key: 'kubernetes', apiKey: 'listKubernetesClusters', labelKey: 'label.kubernetes',   icon: 'ClusterOutlined',        count: this.counts.kubernetes, path: '/kubernetes'      },
        { key: 'volumes',    apiKey: 'listVolumes',            labelKey: 'label.volumes',      icon: 'HddOutlined',            count: this.counts.volumes,    path: '/volume'          },
        { key: 'snapshots',  apiKey: 'listSnapshots',          labelKey: 'label.snapshots',    icon: 'BuildOutlined',          count: this.counts.snapshots,  path: '/snapshot'        },
        { key: 'networks',   apiKey: 'listNetworks',           labelKey: 'label.networks',     icon: 'ApartmentOutlined',      count: this.counts.networks,   path: '/guestnetwork'    },
        { key: 'vpcs',       apiKey: 'listVPCs',               labelKey: 'label.vpcs',         icon: 'DeploymentUnitOutlined', count: this.counts.vpcs,       path: '/vpc'             },
        { key: 'ips',        apiKey: 'listPublicIpAddresses',  labelKey: 'label.public.ips',   icon: 'EnvironmentOutlined',    count: this.counts.ips,        path: '/publicip'        },
        { key: 'templates',  apiKey: 'listTemplates',          labelKey: 'label.templates',    icon: 'PictureOutlined',        count: this.counts.templates,  path: '/template'        }
      ].filter(r => r.apiKey in apis)
    },

    vmColumns () {
      return [
        { title: this.$t('label.name'),      dataIndex: 'name',      key: 'name',      ellipsis: true },
        { title: this.$t('label.state'),     dataIndex: 'state',     key: 'state'                     },
        { title: this.$t('label.resources'), dataIndex: 'cpunumber', key: 'resources'                 },
        { title: this.$t('label.ipaddress'), dataIndex: 'nic',       key: 'ipaddress'                 }
      ]
    },

    k8sColumns () {
      return [
        { title: this.$t('label.name'),    dataIndex: 'name',                    key: 'name'    },
        { title: this.$t('label.state'),   dataIndex: 'state',                   key: 'state'   },
        { title: this.$t('label.size'),    dataIndex: 'size',                    key: 'size'    },
        { title: this.$t('label.zone'),    dataIndex: 'zonename',                key: 'zone',   ellipsis: true },
        { title: 'Version',                dataIndex: 'kubernetesversionname',   key: 'version', ellipsis: true }
      ]
    },

    autoscaleColumns () {
      return [
        { title: this.$t('label.name'),         dataIndex: 'name',     key: 'name'  },
        { title: this.$t('label.state'),         dataIndex: 'state',    key: 'state' },
        { title: this.$t('label.vms') || 'VMs', dataIndex: 'vmcount',  key: 'size'  },
        { title: this.$t('label.zone'),          dataIndex: 'zonename', key: 'zone', ellipsis: true }
      ]
    },

    networkColumns () {
      return [
        { title: this.$t('label.name'),    dataIndex: 'name',    key: 'name'  },
        { title: this.$t('label.state'),   dataIndex: 'state',   key: 'state' },
        { title: this.$t('label.type'),    dataIndex: 'type',    key: 'type'  },
        { title: this.$t('label.cidr'),    dataIndex: 'cidr',    key: 'cidr'  },
        { title: this.$t('label.zone'),    dataIndex: 'zonename',key: 'zone', ellipsis: true }
      ]
    }
  },

  created () {
    this.fetchData()
  },

  watch: {
    '$route' (to) {
      if (to.name === 'dashboard') this.fetchData()
    }
  },

  methods: {
    // ── Capacity helpers ───────────────────────────────────────────────────
    capacityPct (ctype) {
      const s = this.statsMap[ctype]
      if (!s || !s.capacitytotal || s.capacitytotal === 0) return 0
      return Math.min(parseFloat((100.0 * s.capacityused / s.capacitytotal).toFixed(2)), 100)
    },

    getCapacityColor (pct) {
      return pct >= 90 ? '#ff4d4f' : pct >= 75 ? '#faad14' : '#52c41a'
    },

    getCapacityClass (pct) {
      return pct >= 90 ? 'used-critical' : pct >= 75 ? 'used-warning' : ''
    },

    formatMemory (mb) {
      if (!mb) return '0 MB'
      return mb >= 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb + ' MB'
    },

    formatSize (bytes) {
      if (!bytes) return '0 B'
      if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB'
      if (bytes >= 1048576)    return (bytes / 1048576).toFixed(1) + ' MB'
      if (bytes >= 1024)       return (bytes / 1024).toFixed(1) + ' KB'
      return bytes + ' B'
    },

    displayData (dataType, value) {
      if (!value) value = 0
      switch (dataType) {
        case 'CPU':
          return parseFloat(value / 1000.0).toFixed(2) + ' GHz'
        case 'MEMORY':
        case 'STORAGE':
        case 'STORAGE_ALLOCATED':
        case 'SECONDARY_STORAGE':
        case 'BACKUP_STORAGE':
        case 'OBJECT_STORAGE':
        case 'LOCAL_STORAGE': {
          const gib = parseFloat(value / (1024 ** 3)).toFixed(2)
          return gib >= 1024
            ? parseFloat(gib / 1024).toFixed(2) + ' TiB'
            : gib + ' GiB'
        }
      }
      return value
    },

    // ── Data fetching ──────────────────────────────────────────────────────
    fetchData () {
      this.listZones()
      this.listAlerts()
      this.listEvents()
      this.fetchVms()
      this.fetchK8s()
      this.fetchAutoscale()
      this.fetchNetworks()
      this.fetchEvents()
      this.fetchBackups()
    },

    listCapacity (zone, latest = false, additive = false) {
      this.capacityLoading = true
      getAPI('listCapacity', { zoneid: zone.id, fetchlatest: latest }).then(json => {
        const stats = json?.listcapacityresponse?.capacity || []
        for (const stat of stats) {
          if (additive) {
            if (stat.name in this.statsMap) {
              for (const [key, value] of Object.entries(stat)) {
                this.statsMap[stat.name][key] = (this.statsMap[stat.name][key] || 0) + value
              }
            } else {
              this.statsMap[stat.name] = { ...stat }
            }
          } else {
            this.statsMap[stat.name] = stat
          }
        }
      }).finally(() => { this.capacityLoading = false })
    },

    updateData (zone, latest = false) {
      this.statsMap = {}
      if (!zone.id) {
        for (const z of this.zones.slice(1)) this.listCapacity(z, latest, true)
      } else {
        this.listCapacity(this.zoneSelected, latest)
      }

      this.data = { pods: 0, clusters: 0, totalHosts: 0, alertHosts: 0, pools: 0, instances: 0, stopped: 0, systemvms: 0, routers: 0 }
      this.loading = true

      const zid = zone.id
      const p = [
        getAPI('listPods',         { zoneid: zid }).then(j => { this.data.pods       = j?.listpodsresponse?.count || 0 }),
        getAPI('listClusters',     { zoneid: zid }).then(j => { this.data.clusters   = j?.listclustersresponse?.count || 0 }),
        getAPI('listStoragePools', { zoneid: zid }).then(j => { this.data.pools      = j?.liststoragepoolsresponse?.count || 0 }),
        getAPI('listSystemVms',    { zoneid: zid }).then(j => { this.data.systemvms  = j?.listsystemvmsresponse?.count || 0 }),
        getAPI('listRouters',      { zoneid: zid, listall: true, projectid: '-1' }).then(j => { this.data.routers    = j?.listroutersresponse?.count || 0 }),
        getAPI('listHosts',        { zoneid: zid, listall: true, details: 'min', type: 'routing', page: 1, pagesize: 1 }).then(j => { this.data.totalHosts = j?.listhostsresponse?.count || 0 }),
        getAPI('listHosts',        { zoneid: zid, listall: true, details: 'min', type: 'routing', state: 'alert', page: 1, pagesize: 1 }).then(j => { this.data.alertHosts = j?.listhostsresponse?.count || 0 }),
        getAPI('listVirtualMachines', { zoneid: zid, listall: true, projectid: '-1', details: 'min', page: 1, pagesize: 1 }).then(j => { this.data.instances = j?.listvirtualmachinesresponse?.count || 0 }),
        getAPI('listVirtualMachines', { zoneid: zid, listall: true, projectid: '-1', details: 'min', state: 'stopped', page: 1, pagesize: 1 }).then(j => { this.data.stopped   = j?.listvirtualmachinesresponse?.count || 0 })
      ]
      Promise.allSettled(p).finally(() => { this.loading = false })
    },

    listAlerts () {
      this.loading = true
      getAPI('listAlerts', { page: 1, pagesize: 10, listall: true }).then(json => {
        this.alerts = json?.listalertsresponse?.alert || []
      }).finally(() => { this.loading = false })
    },

    listEvents () {
      this.loading = true
      getAPI('listEvents', { page: 1, pagesize: 10, listall: true }).then(json => {
        this.events = json?.listeventsresponse?.event || []
      }).finally(() => { this.loading = false })
    },

    listZones () {
      getAPI('listZones', { showicon: true }).then(json => {
        const zones = json?.listzonesresponse?.zone || []
        if (zones.length > 0) {
          this.zones = [{ name: this.$t('label.all.zone') }, ...zones]
          this.zoneSelected = this.zones[0]
          this.updateData(this.zones[0])
        }
      })
    },

    // ── VMs ───────────────────────────────────────────────────────────────
    fetchVms () {
      if (!('listVirtualMachines' in this.$store.getters.apis)) return
      this.loadingVms = false
      Promise.all([
        getAPI('listVirtualMachines', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listVirtualMachines', { listall: true, page: 1, pagesize: 10 })
      ]).then(([c, l]) => {
        this.vmTotal = c?.listvirtualmachinesresponse?.count || 0
        this.vms     = l?.listvirtualmachinesresponse?.virtualmachine || []
      }).finally(() => { this.loadingVms = false })
    },

    // ── Kubernetes ────────────────────────────────────────────────────────
    fetchK8s () {
      if (!('listKubernetesClusters' in this.$store.getters.apis)) return
      this.loadingK8s = true
      Promise.all([
        getAPI('listKubernetesClusters', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listKubernetesClusters', { listall: true, page: 1, pagesize: 8 })
      ]).then(([c, l]) => {
        this.k8sTotal    = c?.listkubernetesclustersresponse?.count || 0
        this.k8sClusters = l?.listkubernetesclustersresponse?.kubernetescluster || []
      }).finally(() => { this.loadingK8s = false })
    },

    // ── Autoscale groups ──────────────────────────────────────────────────
    fetchAutoscale () {
      if (!('listAutoScaleVmGroups' in this.$store.getters.apis)) return
      this.loadingAutoscale = true
      Promise.all([
        getAPI('listAutoScaleVmGroups', { listall: true, page: 1, pagesize: 1 }),
        getAPI('listAutoScaleVmGroups', { listall: true, page: 1, pagesize: 8 })
      ]).then(([c, l]) => {
        this.autoscaleTotal  = c?.listautoscalevmgroupsresponse?.count || 0
        this.autoscaleGroups = l?.listautoscalevmgroupsresponse?.autoscalevmgroup || []
      }).finally(() => { this.loadingAutoscale = false })
    },

    // ── Networks ──────────────────────────────────────────────────────────
    fetchNetworks () {
      if (!('listNetworks' in this.$store.getters.apis)) return
      this.loadingNetworks = true
      getAPI('listNetworks', { listall: true, page: 1, pagesize: 8 })
        .then(j => { this.networks = j?.listnetworksresponse?.network || [] })
        .finally(() => { this.loadingNetworks = false })
    },

    // ── Events ────────────────────────────────────────────────────────────
    fetchEvents () {
      if (!('listEvents' in this.$store.getters.apis)) return
      this.loadingEvents = true
      getAPI('listEvents', { page: 1, pagesize: 12, listall: true })
        .then(j => { this.events = j?.listeventsresponse?.event || [] })
        .finally(() => { this.loadingEvents = false })
    },

    // ── Backups: try VM snapshots first, fall back to volume snapshots ─────
    fetchBackups () {
      if (!('listBackups' in this.$store.getters.apis)) return
      this.loadingBackups = true
      getAPI('listBackups', { listall: true, page: 1, pagesize: 6 })
        .then(j => { this.backups = j?.listbackupsresponse?.backup || [] })
        .finally(() => { this.loadingBackups = false })
    },

    changeZone (index) {
      this.zoneSelected = this.zones[index]
      this.updateData(this.zoneSelected)
    },

    filterZone (input, option) {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },

    getEventColour (event) {
      if (event.level === 'ERROR')     return 'red'
      if (event.state === 'Completed') return 'green'
      return 'blue'
    }
  }
}
</script>

<style lang="less" scoped>
// ── Greeting ──────────────────────────────────────────────────────────────
.greeting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.greeting-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 2px;
  color: rgba(0, 0, 0, 0.85);
}

.greeting-sub {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0;
}

// ── Capacity bars — same flex pattern as UsageDashboard/UserDashboard ─────
.capacity-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow: hidden;
}

.capacity-item {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 10px;
  border-right: 1px solid #f0f0f0;
  &:first-child { padding-left: 0; }
  &:last-child  { padding-right: 0; border-right: none; }
}

// Compute items get a bit more breathing room
.capacity-item--large {
  flex: 2 1 0;
}

.capacity-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
}

.capacity-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.capacity-used {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  flex-shrink: 0;
  &.used-warning  { color: #faad14; font-weight: 600; }
  &.used-critical { color: #ff4d4f; font-weight: 600; }
}

.capacity-sep { color: #d9d9d9; margin: 0 2px; }

.capacity-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.capacity-free { color: #bfbfbf; }

// ── Infrastructure count pills ────────────────────────────────────────────
.counts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.count-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 20px;
  background: #fafafa;
  color: rgba(0, 0, 0, 0.65);
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s;
  white-space: nowrap;
  &:hover { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }

  &--alert {
    border-color: #ffccc7;
    background: #fff2f0;
    color: #ff4d4f;
    &:hover { border-color: #ff4d4f; background: #fff2f0; }
  }

  &--muted {
    opacity: 0.65;
  }
}

.count-icon  { font-size: 13px; opacity: 0.65; }
.count-value { font-size: 15px; font-weight: 700; color: rgba(0, 0, 0, 0.85); line-height: 1; }
.count-label { font-size: 12px; }
.count-ok    { color: #52c41a; font-size: 12px; }

// ── Compact events / alerts ───────────────────────────────────────────────
.event-list {
  display: flex;
  flex-direction: column;
}

.event-compact-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
}

.event-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  &.dot-red   { background: #ff4d4f; }
  &.dot-green { background: #52c41a; }
  &.dot-blue  { background: #1890ff; }
}

.event-compact-body  { flex: 1; min-width: 0; }

.event-compact-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
}

.event-type {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 65%;
}

.event-compact-time {
  font-size: 10px;
  color: #bbb;
  white-space: nowrap;
  flex-shrink: 0;
}

.event-compact-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ── Tabs ──────────────────────────────────────────────────────────────────
:deep(.ant-tabs-nav) { margin-bottom: 0 !important; }

// ── Shared ────────────────────────────────────────────────────────────────
.ant-col {
  display: flex;
  flex-flow: column;
  padding-bottom: 5px;
}

.ant-card {
  border-radius: 5px;
  flex: 1;
}
</style>